import React from "react";

import { Button, Checkbox, Picto } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useQueryClient } from "@tanstack/react-query";

import TasksService from "@/services/TasksService";

import { useSelection } from "@/hooks/useSelection";

import { DeleteTasksConfirmModal } from "@/components/molecules";

import { isTaskDependentQuery } from "@/constants/CQueryKeys";

import { TTask } from "@/types";

import { promiseToast } from "@/modules/index";

export const SelectedTasksActionsMenu: React.FC = () => {
	const queryClient = useQueryClient();
	const [confirmDelete, setConfirmDelete] = React.useState(false);

	const {
		selected,
		isAllSelected,
		isIndeterminate,
		selectAll,
		clearSelection,
		selectMode,
		setSelectMode,
	} = useSelection<TTask>();

	if (!selected.size && !selectMode) return null;

	const handleSelectAll = () => {
		if (isAllSelected()) {
			setSelectMode(false);
			clearSelection();
		} else {
			selectAll();
		}
	};

	const handleDelete = (fromConfirm = false) => {
		if (!fromConfirm) {
			setConfirmDelete(true);
			return;
		}

		const taskIds = Array.from(selected.values()).map((task) =>
			task.id.toString()
		);

		const prom = TasksService.deleteTasks(taskIds).then(
			() => {
				clearSelection();
				setConfirmDelete(false);
				// Not only the listings: the figures and summaries counted
				// these tasks too (they used to stay stale here).
				queryClient.invalidateQueries({
					predicate: (query) => isTaskDependentQuery(query.queryKey),
				});
			},
			(error) => {
				setConfirmDelete(false);
				return Promise.reject(error);
			}
		);

		promiseToast(prom);

		return prom;
	};

	return (
		<div className="mt-2 flex items-center justify-between border-t pt-2">
			<div className="text-xs font-bold">
				<Checkbox
					label="Tout sélectionner"
					indeterminate={isIndeterminate()}
					checked={selected.size > 0}
					onChange={handleSelectAll}
				/>
			</div>
			<div className="flex items-center gap-4 [&_svg]:h-4">
				{/* <Button
					size="sm"
					onClick={() => {
						clearSelection();
					}}
					color="white"
					variant="outline"
				>
					<Picto icon="copy" />
				</Button>
				<Button size="sm" onClick={() => {}} color="green" outline>
					<Picto icon="archive" />
				</Button> */}
				<Button
					size="sm"
					onClick={() => handleDelete()}
					color="danger"
					variant="outline"
					// Nothing ticked yet: a confirmation for zero tasks, then
					// the API refusing an empty batch, says nothing useful.
					disabled={selected.size === 0}
					aria-label={t("actions.delete")}
				>
					<Picto icon="trash" />
				</Button>
				<Button
					size="sm"
					onClick={() => {
						setSelectMode(false);
						clearSelection();
					}}
					// Solid, not outline: a white outline is white text on
					// the white card — the button was there, invisible.
					color="white"
				>
					{t("actions.cancel")}
				</Button>
			</div>
			<DeleteTasksConfirmModal
				items={Array.from(selected.values())}
				onConfirm={() => handleDelete(true)}
				open={confirmDelete}
				onClose={() => setConfirmDelete(false)}
			/>
		</div>
	);
};
